/**
 * SilkBackground  -  the same WebGL silk-fabric shader from LandingScreen,
 * always fully expanded, rendered as a fixed layer behind the app shell.
 *
 * Why a separate file: LandingScreen couples the shader to its phase state
 * machine and auth form. Extracting the canvas-only concern here lets App
 * mount the background without inheriting any of that logic, and avoids
 * re-running the 2.6-second intro animation every page load.
 *
 * The shader is identical to LandingScreen's. u_expand is pinned to 1.0
 * so the blob mask is always open (no growing-circle reveal). Mouse/touch
 * interaction and film grain are preserved for visual continuity with the
 * landing screen.
 */

import { useEffect, useRef } from 'react';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

const VERT_SRC = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const _sub = getCurrentSubdomain();
const PALETTE = _sub === 'tim'
  ? `  vec3 c0 = vec3(0.003, 0.002, 0.001);
  vec3 c1 = vec3(0.018, 0.013, 0.004);
  vec3 c2 = vec3(0.050, 0.038, 0.010);
  vec3 c3 = vec3(0.115, 0.090, 0.022);
  vec3 c4 = vec3(0.200, 0.155, 0.040);
  vec3 ch = vec3(0.180, 0.140, 0.035);`
  : _sub === 'private' || _sub === 'paul'
  ? `  vec3 c0 = vec3(0.001, 0.008, 0.002);
  vec3 c1 = vec3(0.003, 0.028, 0.006);
  vec3 c2 = vec3(0.005, 0.065, 0.014);
  vec3 c3 = vec3(0.008, 0.108, 0.022);
  vec3 c4 = vec3(0.009, 0.142, 0.028);
  vec3 ch = vec3(0.008, 0.128, 0.025);`
  : `  vec3 c0 = vec3(0.001, 0.007, 0.020);
  vec3 c1 = vec3(0.002, 0.028, 0.068);
  vec3 c2 = vec3(0.004, 0.062, 0.140);
  vec3 c3 = vec3(0.007, 0.102, 0.210);
  vec3 c4 = vec3(0.008, 0.128, 0.255);
  vec3 ch = vec3(0.007, 0.115, 0.238);`;

const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_res;
uniform float u_expand;
uniform vec2  u_mouse;
uniform vec2  u_vel;
uniform float u_gamma;

vec2 gh(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453) * 2.0 - 1.0;
}
float gn(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f*f*(3.0-2.0*f);
  return mix(
    mix(dot(gh(i),           f           ), dot(gh(i+vec2(1,0)), f-vec2(1,0)), u.x),
    mix(dot(gh(i+vec2(0,1)), f-vec2(0,1) ), dot(gh(i+vec2(1,1)), f-vec2(1,1)), u.x),
    u.y
  );
}
float fbm(vec2 p) {
  float v=0.0, a=0.60;
  mat2 R = mat2(1.6, 1.1, -1.1, 1.6);
  v += a*gn(p); p=R*p; a*=0.50;
  v += a*gn(p); p=R*p; a*=0.50;
  v += a*gn(p);
  return v;
}
float hash1(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 st = (gl_FragCoord.xy * 2.0 - u_res) / min(u_res.x, u_res.y);
  float dist = length(st);

  vec2 mPos   = u_mouse * u_res / min(u_res.x, u_res.y);
  vec2 mDelta = st - mPos;
  float mDist = length(mDelta);
  float mFall = exp(-mDist * mDist * 0.42);
  vec2 cursorW = -u_vel * mFall * 0.014;

  float s1x = sin(u_time * 0.031 + 0.00) * 0.90;
  float s1y = cos(u_time * 0.024 + 1.20) * 0.85;
  float s2x = sin(u_time * 0.019 + 2.10) * 0.75;
  float s2y = cos(u_time * 0.027 + 3.40) * 0.80;

  vec2 drift = vec2(
    sin(u_time * 0.055 + 0.80) * 0.022 + sin(u_time * 0.029 + 2.10) * 0.013,
    cos(u_time * 0.041 + 1.50) * 0.019 + cos(u_time * 0.037 + 0.40) * 0.011
  );

  vec2 p = st * 0.30 + cursorW;

  vec2 q = vec2(
    fbm(p + vec2(s1x, s1y) + drift),
    fbm(p + vec2(5.20 + s2x, s2y) + drift)
  );
  float f = fbm(p + 4.5*q + vec2(1.7, 9.2));

  f = f * 0.5 + 0.5;
  f = clamp(f, 0.0, 1.0);
  f = f * f * (3.0 - 2.0 * f);
  f = f * f * (3.0 - 2.0 * f);
  f = pow(f, u_gamma);

  ${PALETTE}

  vec3 col = c0;
  col = mix(col, c1, smoothstep(0.08, 0.46, f));
  col = mix(col, c2, smoothstep(0.25, 0.64, f));
  col = mix(col, c3, smoothstep(0.45, 0.78, f));
  col = mix(col, c4, smoothstep(0.62, 0.91, f) * 0.75);
  col = mix(col, ch, smoothstep(0.80, 1.00, f) * 0.55);

  float intr    = 1.0 - u_expand;
  float breathe = sin(u_time * 0.42) * 0.055 * intr;
  float blobR    = 0.86 + u_expand * 3.30 + breathe;
  float blobSoft = 0.42 + u_expand * 1.08;
  float rawMask  = smoothstep(blobR, blobR - blobSoft, dist);
  col *= pow(rawMask, 1.0 + u_expand * 0.85);

  float grainT = floor(u_time * 24.0);
  float g = hash1(gl_FragCoord.xy + vec2(grainT * 7.31, grainT * 5.17));
  col = clamp(col + g * 0.028 - 0.014, 0.0, 1.0);

  gl_FragColor = vec4(col, 1.0);
}
`;

interface GLCtx {
  gl: WebGLRenderingContext;
  prog: WebGLProgram;
  uTime: WebGLUniformLocation;
  uRes: WebGLUniformLocation;
  uExpand: WebGLUniformLocation;
  uMouse: WebGLUniformLocation | null;
  uVel: WebGLUniformLocation | null;
  uGamma: WebGLUniformLocation | null;
}

function initWebGL(canvas: HTMLCanvasElement): GLCtx | null {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false, preserveDrawingBuffer: true });
  if (!gl) return null;

  function compile(type: number, src: string): WebGLShader | null {
    const s = gl!.createShader(type);
    if (!s) return null;
    gl!.shaderSource(s, src);
    gl!.compileShader(s);
    if (!gl!.getShaderParameter(s, gl!.COMPILE_STATUS)) {
      console.error('Shader error:', gl!.getShaderInfoLog(s));
      return null;
    }
    return s;
  }

  const vert = compile(gl.VERTEX_SHADER, VERT_SRC);
  const frag = compile(gl.FRAGMENT_SHADER, FRAG_SRC);
  if (!vert || !frag) return null;

  const prog = gl.createProgram()!;
  gl.attachShader(prog, vert);
  gl.attachShader(prog, frag);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error('Link error:', gl.getProgramInfoLog(prog));
    return null;
  }
  gl.useProgram(prog);
  gl.deleteShader(vert);
  gl.deleteShader(frag);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  return {
    gl, prog,
    uTime:   gl.getUniformLocation(prog, 'u_time')!,
    uRes:    gl.getUniformLocation(prog, 'u_res')!,
    uExpand: gl.getUniformLocation(prog, 'u_expand')!,
    uMouse:  gl.getUniformLocation(prog, 'u_mouse'),
    uVel:    gl.getUniformLocation(prog, 'u_vel'),
    uGamma:  gl.getUniformLocation(prog, 'u_gamma'),
  };
}

export function SilkBackground(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);
  const t0Ref     = useRef(performance.now());
  const mouseRef  = useRef({ x: 0, y: 0, tx: 0, ty: 0, init: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = initWebGL(canvas);

    function resize(): void {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width  = Math.round(window.innerWidth  * dpr);
      canvas!.height = Math.round(window.innerHeight * dpr);
      if (ctx) ctx.gl.viewport(0, 0, canvas!.width, canvas!.height);
    }
    resize();
    window.addEventListener('resize', resize);

    function onMouseMove(e: MouseEvent): void {
      const m = mouseRef.current;
      const nx =  (e.clientX / window.innerWidth  - 0.5) * 2;
      const ny = -(e.clientY / window.innerHeight - 0.5) * 2;
      if (!m.init) { m.x = nx; m.y = ny; m.init = true; }
      m.tx = nx; m.ty = ny;
    }
    function onTouchMove(e: TouchEvent): void {
      const touch = e.touches[0];
      if (!touch) return;
      const m = mouseRef.current;
      const nx =  (touch.clientX / window.innerWidth  - 0.5) * 2;
      const ny = -(touch.clientY / window.innerHeight - 0.5) * 2;
      if (!m.init) { m.x = nx; m.y = ny; m.init = true; }
      m.tx = nx; m.ty = ny;
    }
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    function render(): void {
      const now = performance.now();
      const t   = (now - t0Ref.current) / 1000;

      const m = mouseRef.current;
      m.x += (m.tx - m.x) * 0.028;
      m.y += (m.ty - m.y) * 0.028;

      if (ctx) {
        const { gl, uTime, uRes, uExpand, uMouse, uVel, uGamma } = ctx;
        gl.uniform1f(uTime,   t);
        gl.uniform2f(uRes,    canvas!.width, canvas!.height);
        gl.uniform1f(uExpand, 1.0); // always fully expanded  -  no blob-mask reveal
        if (uMouse) gl.uniform2f(uMouse, m.x, m.y);
        if (uVel)   gl.uniform2f(uVel, m.tx - m.x, m.ty - m.y);
        if (uGamma) {
          const aspect = canvas!.height / canvas!.width;
          const gamma  = aspect > 1 ? Math.max(1.10, 1.40 / aspect) : 1.40;
          gl.uniform1f(uGamma, gamma);
        }
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }

      rafRef.current = requestAnimationFrame(render);
    }
    render();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      if (ctx) ctx.gl.deleteProgram(ctx.prog);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed', inset: 0,
          width: '100%', height: '100%',
          display: 'block',
          zIndex: 0,
          background: '#000000',
        }}
      />
      {/* Vignette: matches LandingScreen's edge-deepening overlay. */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0,
          background: 'radial-gradient(ellipse 88% 88% at 50% 50%, transparent 35%, rgba(0,0,0,0.52) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
    </>
  );
}
